<script>
    import { onDestroy } from 'svelte';
    import { iconFor, slugFor } from './tarotIcons.js';
    import {
        rowsFor,
        baselineReadings,
        menuGroupsFor,
        findOption,
        optionKey,
        auraGroupsFor,
    } from './modifierHypothetical.js';
    import { createReadingsLoader } from './hypotheticalRequest.js';

    export let effects;
    export let roleName;
    export let roleSlug;
    export let fetchImpl = null;
    export let requestDelay = 150;

    const loader = createReadingsLoader({ fetch: fetchImpl, delay: requestDelay });

    let loadout = [];
    let pending = '';
    let filter = '';
    let readings = baselineReadings(effects);
    let failure = null;
    let loading = false;

    $: menuGroups = menuGroupsFor(effects, loadout, filter, roleName);
    $: rows = rowsFor(effects, readings);
    $: unsimulated = (readings && readings.unsimulated) || [];
    $: auraGroups = auraGroupsFor(readings);
    $: hasAuras = auraGroups.visible.length > 0 || auraGroups.hidden.length > 0;
    $: auraWording = (effects && effects.auraWording) || {};

    // The wording is server-owned so the role page and the aura pages cannot describe the same
    // split differently. Only the hidden group carries a note: that one is the surprising half.
    $: auraGroupList = [
        {
            key: 'visible',
            heading: auraWording.visibleHeading,
            auras: auraGroups.visible,
            note: null,
        },
        {
            key: 'hidden',
            heading: auraWording.hiddenHeading,
            auras: auraGroups.hidden,
            note: auraWording.hiddenNote,
        },
    ];
    $: intelChanged = rows.some((row) => row.changed);
    $: bearerNoticed = rows.some((row) => row.bearerChanged);

    // The "you are told" column only earns its width when it can disagree with the intel
    // reading: either it already does at baseline, as it does for a Lycan, or a loadout is in
    // play that could move one half without the other.
    $: bearerDiffers = rows.some((row) => row.bearerBefore !== row.before);
    $: showBearer = loadout.length > 0 || bearerDiffers;

    function refresh() {
        failure = null;

        if (loadout.length === 0) {
            loader.cancel();
            loading = false;
            readings = baselineReadings(effects);
            return;
        }

        loading = true;
        loader.load(roleSlug, loadout, {
            onReadings: (result) => {
                readings = result;
                loading = false;
            },
            onError: (error) => {
                failure = error.message;
                loading = false;
            },
        });
    }

    function add(key) {
        const option = findOption(effects, key);
        if (!option) return;

        loadout = loadout.concat(option);
        filter = '';
        refresh();
    }

    function remove(key) {
        loadout = loadout.filter((entry) => optionKey(entry) !== key);
        refresh();
    }

    function clear() {
        loadout = [];
        filter = '';
        refresh();
    }

    function pick(event) {
        const key = event.currentTarget.value;
        pending = '';
        if (key) add(key);
    }

    onDestroy(() => loader.cancel());
</script>

<div class="guide-readings">
    {#if failure}
        <p class="guide-status-note guide-hypothetical-warning" role="alert">{failure}</p>
    {:else}
        <div class="guide-table-scroll" class:guide-hypothetical-loading={loading}>
            <table class="guide-status-table guide-readings-table">
                <thead>
                    <tr>
                        <th scope="col">Status</th>
                        <th scope="col">Reads as</th>
                        {#if showBearer}
                            <th scope="col">You are told</th>
                        {/if}
                        <th scope="col">Tarot card</th>
                    </tr>
                </thead>
                <tbody>
                    {#each rows as row (row.label)}
                        <tr class:guide-reading-row--changed={row.changed}>
                            <th scope="row">{row.label}</th>
                            <td>
                                {row.after}
                                {#if row.changed}
                                    <span class="guide-reading-flag">changed</span>
                                    <span class="guide-reading-was">was {row.before}</span>
                                {/if}
                                {#if row.cause}
                                    <span class="guide-reading-cause">changed by {row.cause}</span>
                                {/if}
                            </td>
                            {#if showBearer}
                                <td>
                                    {row.bearerAfter}
                                    {#if row.bearerChanged}
                                        <span class="guide-reading-flag">you are told</span>
                                    {:else if row.changed}
                                        <span class="guide-reading-flag guide-reading-flag--muted"
                                            >you are not told</span
                                        >
                                    {/if}
                                    {#if row.bearerCause}
                                        <span class="guide-reading-cause"
                                            >changed by {row.bearerCause}</span
                                        >
                                    {/if}
                                </td>
                            {/if}
                            <td>
                                <span
                                    class="guide-badge-chip guide-badge-chip--{slugFor(
                                        row.afterCard,
                                    )}"
                                    title={row.afterCard}
                                >
                                    <i class={iconFor(row.afterCard)} aria-hidden="true"></i>
                                    <span class="guide-badge-chip-name">{row.afterCard}</span>
                                </span>
                                {#if row.cardChanged}
                                    <span class="guide-reading-was">was {row.beforeCard}</span>
                                {/if}
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>
    {/if}

    <p class="guide-status-note">
        {#if loadout.length === 0}
            Add items and auras below to see what an intel check would read off the {roleName}, and
            what the {roleName} would be told about themselves.
        {:else if !intelChanged}
            Nothing in this set changes what an intel check reads off the {roleName}.
        {:else if bearerNoticed}
            The {roleName}'s own player panel changes too, so they would know something had changed
            how they read.
        {:else}
            Nothing on the {roleName}'s own player panel changes, so they would have no way to tell
            any of this had happened.
        {/if}
    </p>

    <div class="guide-hypothetical">
        <label class="guide-hypothetical-label" for="guide-hypothetical-select">
            Give the {roleName} items and auras
        </label>
        <input
            id="guide-hypothetical-filter"
            class="guide-hypothetical-filter"
            type="search"
            placeholder="Filter the list"
            aria-label="Filter items and auras"
            bind:value={filter}
        />

        <select
            id="guide-hypothetical-select"
            class="guide-hypothetical-select"
            bind:value={pending}
            on:change={pick}
        >
            <option value="">Add an item or aura</option>
            {#each menuGroups as group (group.label)}
                <optgroup label={group.label}>
                    {#each group.options as option (optionKey(option))}
                        <option value={optionKey(option)}>{option.displayName}</option>
                    {/each}
                </optgroup>
            {/each}
        </select>

        {#if menuGroups.length === 0}
            <p class="guide-status-note guide-hypothetical-nomatch">
                {#if filter.trim() === ''}
                    Everything the guide publishes is already in the set.
                {:else}
                    Nothing left to add matches "{filter}".
                {/if}
            </p>
        {/if}

        {#if loadout.length > 0}
            <ul class="guide-hypothetical-loadout">
                {#each loadout as entry (optionKey(entry))}
                    <li class="guide-hypothetical-chip">
                        <a href={entry.url}>{entry.displayName}</a>
                        <button
                            type="button"
                            class="guide-hypothetical-chip-remove"
                            aria-label="Remove {entry.displayName}"
                            on:click={() => remove(optionKey(entry))}>&times;</button
                        >
                    </li>
                {/each}
                <li>
                    <button type="button" class="guide-hypothetical-clear" on:click={clear}>
                        Clear all
                    </button>
                </li>
            </ul>
        {/if}

        {#if hasAuras}
            <div
                class="guide-resulting-auras"
                class:guide-resulting-auras--incomplete={unsimulated.length > 0}
            >
                <p class="guide-resulting-auras-lead">
                    {#if unsimulated.length > 0}
                        This set leaves the {roleName} carrying, as far as the engine could work out:
                    {:else if loadout.length === 0}
                        The {roleName} carries these before anything is added.
                    {:else}
                        This set leaves the {roleName} carrying:
                    {/if}
                </p>

                {#each auraGroupList as group (group.key)}
                    {#if group.auras.length > 0}
                        <div class="guide-resulting-auras--{group.key}">
                            <h4 class="guide-resulting-auras-heading">{group.heading}</h4>
                            <ul class="guide-resulting-aura-list">
                                {#each group.auras as aura (aura.name)}
                                    <li
                                        class="guide-resulting-aura"
                                        class:guide-resulting-aura--unpublished={!aura.url}
                                    >
                                        {#if aura.url}
                                            <a href={aura.url}>{aura.displayName}</a>
                                        {:else}
                                            {aura.displayName}
                                        {/if}
                                    </li>
                                {/each}
                            </ul>
                            {#if group.note}
                                <p class="guide-status-note guide-resulting-auras-note">
                                    {group.note}
                                </p>
                            {/if}
                        </div>
                    {/if}
                {/each}
            </div>
        {/if}

        {#if unsimulated.length > 0}
            <p class="guide-status-note guide-hypothetical-warning">
                The engine has no way to apply {unsimulated.join(', ')}, so nothing above accounts
                for {unsimulated.length === 1 ? 'it' : 'them'}.
            </p>
        {/if}
    </div>
</div>
